import { useCallback } from "react";
import { capturePostHog } from "./posthog";

// Development mode detection
const isDevelopment = import.meta.env.DEV;

// Build PostHog event properties from a (value, metadata) pair. Metadata is
// spread first so the explicit `value` argument always wins over a colliding
// `metadata.value` key.
function buildProperties(
	value?: string | number,
	metadata?: Record<string, unknown>,
): Record<string, unknown> {
	return {
		...metadata,
		...(value !== undefined ? { value } : {}),
	};
}

function captureEvent(
	eventName: string,
	value?: string | number,
	metadata?: Record<string, unknown>,
): void {
	// Log all events in development for debugging visibility
	if (isDevelopment) {
		const valueStr = value !== undefined ? ` (${value})` : "";
		const metaStr = metadata ? ` ${JSON.stringify(metadata)}` : "";
		console.log(`[PostHog] ${eventName}${valueStr}${metaStr}`);
	}

	try {
		// capturePostHog itself decides whether to send, buffer (while init is in
		// flight post-auth), or no-op — so early post-auth events aren't dropped.
		capturePostHog(eventName, buildProperties(value, metadata));
	} catch (error) {
		console.error("[PostHog] Error logging event:", error);
	}
}

// Custom hook for analytics
export function useAnalytics() {
	const logEvent = useCallback(
		(
			eventName: string,
			value?: string | number,
			metadata?: Record<string, unknown>,
		) => {
			captureEvent(eventName, value, metadata);
		},
		[],
	);

	const logPageView = useCallback(
		(page: string) => {
			logEvent("page_view", page, {
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	const logUserAction = useCallback(
		(action: string, metadata?: Record<string, unknown>) => {
			logEvent("user_action", action, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Auth/session events. Pass the bare descriptive name (e.g. "login_initiated",
	// "callback_status_fetched", "2fa_code_sent", "logout") — it is emitted to
	// PostHog as the real event name, namespaced under `auth_` so the whole auth
	// funnel groups together. This replaces the legacy generic "auth_event" (which
	// buried the descriptive name in a `value` prop and showed up undifferentiated
	// in PostHog).
	const logAuth = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`auth_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Onboarding events. Pass the bare descriptive name (e.g. "screen_viewed",
	// "fertility_role_selected", "complete_clicked") — it is emitted to PostHog as
	// the real event name, namespaced under `onboarding_` so the whole onboarding
	// funnel groups together (same approach as `logAuth`). Replaces the earlier
	// mix of bare names and ad-hoc prefixes.
	const logOnboarding = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`onboarding_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Home page events. Pass the bare descriptive name (e.g. "page_viewed",
	// "insight_clicked") — emitted under the `home_` prefix so the home section
	// groups together.
	const logHome = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`home_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// App header / chrome events (hamburger, logo, beta tag, profile dropdown,
	// header notifications). Pass the bare descriptive name (e.g. "logo_clicked")
	// — emitted under the `header_` prefix so the app-header chrome groups
	// together (parallels `onboarding_` for the onboarding header).
	const logHeader = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`header_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Journey events (journey page: step pills, tasks, FAQs, Ask Eve / Call Nurse,
	// provider booking). Pass the bare descriptive name (e.g. "task_opened",
	// "booking_confirmed") — emitted under the `journey_` prefix so the whole
	// journey funnel groups together.
	const logJourney = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`journey_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Eve AI chat events (chat page, message send/receive, attachments, voice,
	// suggested questions, message actions, chat history). Pass the bare
	// descriptive name (e.g. "message_sent", "page_viewed") — emitted under the
	// `chat_` prefix. NEVER pass message content here (medical free-text); only
	// interaction metadata (length, counts, ids).
	const logChat = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`chat_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Communication events (provider/expert: consult-an-expert, provider chat,
	// digital nurse, meeting booking). Pass the bare descriptive name (e.g.
	// "booking_confirmed") — emitted under the `communication_` prefix. NEVER
	// pass provider-chat message content (medical free-text); only interaction
	// metadata (length, counts, ids, enums).
	const logCommunication = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`communication_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Education Hub events (content cards, library, articles, flashcards,
	// podcasts). Pass the bare descriptive name (e.g. "card_opened",
	// "podcast_play_toggled") — emitted under the `education_` prefix.
	const logEducation = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`education_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Track / Calendar events. UNLIKE the other section wrappers, this is a
	// PASSTHROUGH: it takes the already-`track_`-prefixed name from the typed
	// registry in `src/lib/track/analyticsEvents.ts` (e.g. TRACK_EVENT_CREATED =
	// "track_event_created") and only adds `timestamp` + `platform`, so the
	// PHI-safe payload contracts and the `scripts/test-track.ts` gates that depend
	// on that registry stay intact. PostHog preserves native prop types (counts
	// stay numbers). NEVER pass medical free-text/values — enums, counts, ids,
	// booleans only.
	const logTrack = useCallback(
		(eventName: string, metadata?: Record<string, unknown>) => {
			logEvent(eventName, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Get Ready events (task list, task cards, custom tasks, and the book /
	// sign-consent / upload task sub-pages). Pass the bare descriptive name
	// (e.g. "task_pressed", "page_viewed") — emitted under the `get_ready_`
	// prefix.
	const logGetReady = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`get_ready_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Profile & settings events (profile detail editing, avatar, security &
	// support settings, account deletion). Pass the bare descriptive name (e.g.
	// "page_viewed", "image_uploaded") — emitted under the `profile_` prefix.
	const logProfile = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`profile_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Pricing & subscription events (plans, billing toggle, purchase funnel,
	// subscription management). Pass the bare descriptive name (e.g.
	// "purchase_completed") — emitted under the `pricing_` prefix. Payment details
	// are NEVER sent — only plan/billing enums, tiers, and structured errors.
	const logPricing = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`pricing_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Navigation events (bottom nav, top quick-links). Pass the bare descriptive
	// name (e.g. "item_clicked") — emitted under the `nav_` prefix so all nav
	// interactions group together; a `bar` property distinguishes which nav.
	const logNav = useCallback(
		(event: string, metadata?: Record<string, unknown>) => {
			logEvent(`nav_${event}`, undefined, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	// Cross-surface UI events that aren't owned by one section (side menu, language
	// selector, feedback modal). They keep their own descriptive name + a `context`
	// property instead of a domain prefix, but still get the same base props
	// (`timestamp`/`platform`) as the section wrappers so all client events are
	// consistent in PostHog. Accepts an optional `value` (e.g. language code).
	const logShared = useCallback(
		(
			event: string,
			value?: string | number,
			metadata?: Record<string, unknown>,
		) => {
			logEvent(event, value, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	const logError = useCallback(
		(error: string, metadata?: Record<string, unknown>) => {
			logEvent("error_event", error, {
				...metadata,
				timestamp: Date.now(),
				platform: "era-web",
			});
		},
		[logEvent],
	);

	return {
		logEvent,
		logPageView,
		logUserAction,
		logAuth,
		logOnboarding,
		logHome,
		logHeader,
		logJourney,
		logChat,
		logCommunication,
		logEducation,
		logTrack,
		logGetReady,
		logProfile,
		logPricing,
		logNav,
		logShared,
		logError,
	};
}

// Export the Analytics type for use in other services
export type Analytics = ReturnType<typeof useAnalytics>;

// Standalone analytics functions for use outside React components
export const analytics = {
	logEvent: (
		eventName: string,
		value?: string | number,
		metadata?: Record<string, unknown>,
	) => {
		captureEvent(eventName, value, metadata);
	},
};
